import classNames from 'classnames';
import { inflect } from 'inflection';
import React from 'react';
import { useDragDropManager } from 'react-dnd';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import archiveIcon from '../../icons/non-prefilled/16x16/archive.svg';
import historyIcon from '../../icons/non-prefilled/16x16/history.svg';
import pinOutlineIcon from '../../icons/non-prefilled/16x16/pinOutline.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpinOutlineIcon from '../../icons/non-prefilled/16x16/unpinOutline.svg';
import PostDetailPreviewPopOver from '../../post/PostDetailPreviewPopOver';
import PostTitleWithIcons from '../../post/PostTitleWithIcons';
import postPath from '../../utils/url/postPath';
import {
  isReorganizationPostEdgeArchived,
  isReorganizationPostEdgeDeleted,
  ReorganizationPostEdgeAction,
  reorganizationPostEdgeActionCheck,
} from '../useTopicReorganization';
import type {
  ReorganizationPostEdge,
  SelectedPosts,
  TopicReorganizationMetadata,
  TopicReorganizationTree,
} from '../useTopicReorganization';
import TopicTreeCardDropSeparator from './TopicTreeCardDropSeparator';
import TopicTreeCardItem from './TopicTreeCardItem';
import TopicTreeCardItemDraggable from './TopicTreeCardItemDraggable';
import * as styles from './TopicTreeCardPost.module.scss';
import TopicTreeCardPostIcon from './TopicTreeCardPostIcon';
import isReorganizationPostEdgePinned from '../utils/isReorganizationPostEdgePinned';
import { DragTypes, MoveStates } from '../constants';
import type { ContentMapGetDropParams, DragObject } from '../utils/types';
import { PostType } from '../../graphql/generated/graphql';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated';

const POST_FRAGMENT = gql(`
  fragment TopicTreeCardPost_post on Post {
    id
    type
    ...postPath_post
    ...PostTitleWithIcons_post
  }
`);

interface ActionMenuItemProps {
  danger?: boolean;
  title: string | ((targets: string[]) => string);
  topicId: string;
  selectedPosts: SelectedPosts;
  action: ReorganizationPostEdgeAction;
  reorganizationPostEdge: ReorganizationPostEdge;
  separator?: boolean;
  onItemClick: (targets: string[]) => void;
  icon?: React.ReactNode;
}

const ActionMenuItem = ({
  title,
  topicId,
  selectedPosts,
  action,
  reorganizationPostEdge,
  separator,
  onItemClick,
  ...props
}: ActionMenuItemProps) => {
  let targets: string[] = [];
  if (reorganizationPostEdge.isSelected) {
    targets = selectedPosts.byAllowedActions[action];
  } else if (
    reorganizationPostEdgeActionCheck(reorganizationPostEdge, topicId, action)
  ) {
    targets = [reorganizationPostEdge.postKey];
  }
  if (!targets.length) return null;

  const menuItemTitle = typeof title === 'function' ? title(targets) : title;
  return (
    <>
      {separator && <MenuSeparator />}
      <MenuItem
        {...props}
        onClick={() => {
          onItemClick(targets);
        }}
      >
        {reorganizationPostEdge.isSelected && selectedPosts.all.length > 0
          ? `${menuItemTitle} (${targets.length})`
          : menuItemTitle}
      </MenuItem>
    </>
  );
};

export interface TopicTreeCardPostCallbacks {
  onPostSelectedChange: (args: { postKey: string; selected: boolean }) => void;
  onPostDeletedChange: (args: { postKeys: string[]; deleted: boolean }) => void;
  onPostPinnedChange: (args: { postKeys: string[]; pinned: boolean }) => void;
  onPostArchivedChange: (args: {
    postKeys: string[];
    archived: boolean;
  }) => void;
}

interface TopicTreeCardPostProps extends TopicTreeCardPostCallbacks {
  reorganizationPostEdge: ReorganizationPostEdge;
  post: DocumentType<typeof POST_FRAGMENT>;
  reorganizable: boolean;
  ancestorsReorganizationMetadata: TopicReorganizationMetadata[];
  index: number;
  lastPinnedPostIndex: number;
  columnSelected: DragObject['toMove'];
  selectedPosts: SelectedPosts;
  getDropParams: ContentMapGetDropParams;
  isReorganizing: boolean;
  tree: TopicReorganizationTree;
}

const TopicTreeCardPost = ({
  reorganizationPostEdge,
  post,
  reorganizable,
  ancestorsReorganizationMetadata,
  index,
  lastPinnedPostIndex,
  columnSelected,
  selectedPosts,
  getDropParams,
  isReorganizing,
  onPostSelectedChange,
  onPostDeletedChange,
  onPostPinnedChange,
  onPostArchivedChange,
  tree,
}: TopicTreeCardPostProps) => {
  const dragDropManager = useDragDropManager();
  const isDragging = dragDropManager.getMonitor().isDragging() ?? false;

  const getSelected = (p: ReorganizationPostEdge) =>
    isReorganizationPostEdgePinned(p)
      ? { postKeys: [], pinnedPostKeys: [p.postKey], topicIds: [] }
      : { postKeys: [p.postKey], pinnedPostKeys: [], topicIds: [] };

  const { reorganizationMetadata } = reorganizationPostEdge;
  const isMovedOut = reorganizationMetadata.move.state === MoveStates.movedOut;

  const isDeleted = isReorganizationPostEdgeDeleted(reorganizationPostEdge);
  const isPinned = isReorganizationPostEdgePinned(reorganizationPostEdge);
  const isArchived = isReorganizationPostEdgeArchived(reorganizationPostEdge);

  return (
    <PostDetailPreviewPopOver
      postId={post.id}
      controls={{ preventOpening: isDragging }}
    >
      <TopicTreeCardItem
        type="post"
        reorganizationMetadata={reorganizationMetadata}
        ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
        titleLine={
          <>
            {isReorganizing && index <= lastPinnedPostIndex && (
              <TopicTreeCardDropSeparator
                {...getDropParams([DragTypes.pinnedPosts], {
                  destinationTree: tree,
                  pinnedPostIndexWithPlaceholder: index,
                })}
              />
            )}
            <TopicTreeCardItemDraggable
              type="post"
              to={postPath(post)}
              onCheckboxChange={() =>
                onPostSelectedChange({
                  postKey: reorganizationPostEdge.postKey,
                  selected: !reorganizationPostEdge.isSelected,
                })
              }
              reorganizable={reorganizable}
              isChecked={reorganizationPostEdge.isSelected}
              isCheckboxDisabled={reorganizationPostEdge.isParentSelected}
              isReorganizing={isReorganizing}
              isMovedOut={isMovedOut}
              toMove={
                reorganizationPostEdge.isSelected
                  ? columnSelected
                  : getSelected(reorganizationPostEdge)
              }
            >
              <div
                className={classNames(styles.content, {
                  [styles.deleted]: isDeleted,
                  [styles.archived]: isArchived,
                })}
              >
                <TopicTreeCardPostIcon
                  pinned={isPinned}
                  archived={isArchived}
                  template={post.type === PostType.Template}
                  movedOut={isMovedOut}
                />
                <div className={styles.title}>
                  <PostTitleWithIcons
                    post={post}
                    iconSize="small"
                    truncate="single"
                  />
                </div>
              </div>
            </TopicTreeCardItemDraggable>
            {isReorganizing && index === lastPinnedPostIndex && (
              <TopicTreeCardDropSeparator
                {...getDropParams([DragTypes.pinnedPosts], {
                  destinationTree: tree,
                  pinnedPostIndexWithPlaceholder: lastPinnedPostIndex + 1,
                })}
              />
            )}
          </>
        }
        menu={
          isReorganizing &&
          reorganizable &&
          tree.topic.id &&
          !isMovedOut && (
            <>
              <ActionMenuItem
                title="Restore"
                icon={historyIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={ReorganizationPostEdgeAction.restore}
                reorganizationPostEdge={reorganizationPostEdge}
                onItemClick={(targets) => {
                  onPostDeletedChange({
                    postKeys: targets,
                    deleted: false,
                  });
                }}
              />
              <ActionMenuItem
                title="Pin"
                icon={pinOutlineIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={ReorganizationPostEdgeAction.pin}
                reorganizationPostEdge={reorganizationPostEdge}
                onItemClick={(targets) => {
                  onPostPinnedChange({
                    postKeys: targets,
                    pinned: true,
                  });
                }}
              />
              <ActionMenuItem
                title="Unpin"
                icon={unpinOutlineIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={ReorganizationPostEdgeAction.unpin}
                reorganizationPostEdge={reorganizationPostEdge}
                onItemClick={(targets) =>
                  onPostPinnedChange({
                    postKeys: targets,
                    pinned: false,
                  })
                }
              />
              <ActionMenuItem
                title="Archive"
                icon={archiveIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={ReorganizationPostEdgeAction.archive}
                reorganizationPostEdge={reorganizationPostEdge}
                onItemClick={(targets) => {
                  onPostArchivedChange({
                    postKeys: targets,
                    archived: true,
                  });
                }}
              />
              <ActionMenuItem
                title="Unarchive"
                icon={unarchiveIcon}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={ReorganizationPostEdgeAction.unarchive}
                reorganizationPostEdge={reorganizationPostEdge}
                onItemClick={(targets) => {
                  onPostArchivedChange({
                    postKeys: targets,
                    archived: false,
                  });
                }}
              />
              <ActionMenuItem
                danger
                separator
                title={(targets) => `Delete ${inflect('post', targets.length)}`}
                topicId={tree.topic.id}
                selectedPosts={selectedPosts}
                action={ReorganizationPostEdgeAction.delete}
                reorganizationPostEdge={reorganizationPostEdge}
                onItemClick={(targets) => {
                  onPostDeletedChange({
                    postKeys: targets,
                    deleted: true,
                  });
                }}
              />
            </>
          )
        }
      />
    </PostDetailPreviewPopOver>
  );
};

export default TopicTreeCardPost;
